import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PasswordInput from "@/components/PasswordInput";
import { Plus, Trash2, UserCog } from "lucide-react";
import { toast } from "sonner";

// What each capability unlocks — keys must match backend security.CAPABILITIES.
const CAPS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "orders", label: "Orders (live board + history)" },
  { key: "floor", label: "Floor & payments" },
  { key: "menu", label: "Menu & categories" },
  { key: "tables", label: "Tables & QR" },
  { key: "waiter", label: "Waiter calls" },
  { key: "reviews", label: "Reviews" },
  { key: "customers", label: "Customers" },
  { key: "settings", label: "Settings" },
];

const DEFAULT_PERMS = { dashboard: true, orders: true, floor: true, waiter: true };

// Human-readable labels for audit actions.
const ACTION_LABEL = {
  "upi.change_requested": "Requested UPI change",
  "upi.change_confirmed": "Confirmed UPI change",
  "export.customers": "Exported customers CSV",
  "export.orders": "Exported orders CSV",
  "order.marked_paid": "Marked order paid",
  "table.settled": "Settled a table",
  "staff.created": "Added a manager",
  "staff.deleted": "Removed a manager",
  "restaurant.disabled": "Disabled restaurant",
  "restaurant.enabled": "Enabled restaurant",
  "restaurant.deleted": "Deleted restaurant",
};

// Owner-visible audit trail — who did the sensitive things (payouts, exports, money, staff).
function ActivityLog() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setRows(r.data)).catch(() => {}); }, []);
  if (!rows.length) return null;
  const when = (iso) => { const d = new Date(iso); return isNaN(d) ? "" : d.toLocaleString([], { day: "2-digit", month: "short", hour: "numeric", minute: "2-digit" }); };
  return (
    <div className="mt-10" data-testid="activity-log">
      <header className="mb-4">
        <div className="text-xs uppercase tracking-widest text-gray-400">Accountability</div>
        <h2 className="font-display text-xl font-bold tracking-tight mt-1">Activity log</h2>
      </header>
      <div className="qd-admin-card overflow-x-auto">
        <table className="w-full text-sm min-w-[520px]">
          <thead className="bg-gray-50 text-xs uppercase tracking-widest text-gray-500">
            <tr><th className="text-left p-3">When</th><th className="text-left p-3">Action</th><th className="text-left p-3">By</th><th className="text-left p-3">Detail</th></tr>
          </thead>
          <tbody>
            {rows.map((e, i) => (
              <tr key={i} className="border-t border-gray-100">
                <td className="p-3 text-gray-500 whitespace-nowrap">{when(e.at)}</td>
                <td className="p-3 font-medium">{ACTION_LABEL[e.action] || e.action}</td>
                <td className="p-3 text-gray-600">{e.actor?.email || (e.actor?.kind === "email_link" ? "email confirm" : e.actor?.kind || "—")}</td>
                <td className="p-3 text-gray-500 truncate max-w-[220px]">{e.target || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminStaff() {
  const auth = useAdminAuth();
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", permissions: { ...DEFAULT_PERMS } });
  const [busy, setBusy] = useState(false);
  const isOwner = ["owner", "super_admin"].includes(auth?.user?.role);

  const load = () => api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
  useEffect(() => { if (isOwner) load(); }, [isOwner]);

  if (!isOwner) return <div className="text-sm text-gray-500 p-8">Only owners can manage staff.</div>;

  const create = async () => {
    if (!form.name.trim() || !form.email.trim())
      return toast.error("Name and email are required");
    if (form.password && form.password.length < 6)
      return toast.error("Password must be at least 6 characters (or leave it blank to email an invite)");
    setBusy(true);
    try {
      const r = await api.post("/admin/staff", form);
      toast.success(r.data.invited ? "Manager invited — set-password link emailed (valid 24h)" : "Manager account created");
      setOpen(false); setForm({ name: "", email: "", password: "", permissions: { ...DEFAULT_PERMS } });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const togglePerm = async (m, key) => {
    const permissions = { ...(m.permissions || {}), [key]: !m.permissions?.[key] };
    try { await api.patch(`/admin/staff/${m.id}`, { permissions }); load(); }
    catch { toast.error("Update failed"); }
  };

  const toggleActive = async (m) => {
    try {
      await api.patch(`/admin/staff/${m.id}`, { is_active: !(m.is_active !== false) });
      toast.success(m.is_active !== false ? `${m.name} deactivated — can no longer sign in` : `${m.name} activated`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const del = async (m) => {
    if (!window.confirm(`Remove ${m.name}'s account?`)) return;
    try { await api.delete(`/admin/staff/${m.id}`); toast.success("Removed"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const resendInvite = async (m) => {
    try {
      await api.post(`/admin/staff/${m.id}/resend-invite`);
      toast.success(`Fresh invite emailed to ${m.email} (valid 24h)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-400">Team</div>
          <h1 className="font-display text-2xl font-bold tracking-tight mt-1 inline-flex items-center gap-2"><UserCog className="w-6 h-6" aria-hidden /> Staff</h1>
        </div>
        <Button data-testid="staff-add-btn" onClick={() => setOpen(true)} className="rounded-lg bg-[var(--qd-charcoal)] hover:bg-black"><Plus className="w-4 h-4 mr-1" aria-hidden /> Add manager</Button>
      </header>

      <div className="space-y-4">
        {staff.map((m) => (
          <div key={m.id} data-testid={`staff-card-${m.id}`} className={`qd-admin-card p-5 ${m.is_active === false ? "opacity-60" : ""}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-semibold">
                  {m.name} <span className="text-xs font-normal text-gray-400">· manager</span>
                  {m.pending && <span data-testid={`staff-pending-${m.id}`} className="ml-2 align-middle text-[9px] font-bold uppercase tracking-wider bg-amber-50 text-amber-600 border border-amber-200 px-1.5 py-0.5 rounded">Invite pending</span>}
                </div>
                <div className="text-sm text-gray-500">
                  {m.email}
                  {m.pending && <button data-testid={`staff-resend-${m.id}`} onClick={() => resendInvite(m)} className="ml-2 text-xs font-semibold text-[var(--qd-primary)]">Resend invite</button>}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-1.5">
                  <Switch data-testid={`staff-active-${m.id}`} checked={m.is_active !== false} onCheckedChange={() => toggleActive(m)} />
                  <span className={`text-xs font-semibold ${m.is_active !== false ? "text-green-700" : "text-gray-400"}`}>{m.is_active !== false ? "Active" : "Inactive"}</span>
                </label>
                <button data-testid={`staff-delete-${m.id}`} onClick={() => del(m)} className="p-1.5 text-gray-500 hover:text-red-600" aria-label={`Remove ${m.name}`}><Trash2 className="w-4 h-4" aria-hidden /></button>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-gray-100">
              <div className="text-[11px] uppercase tracking-widest text-gray-400 mb-2">Can access</div>
              <div className="flex flex-wrap gap-2">
                {CAPS.map((c) => {
                  const on = !!m.permissions?.[c.key];
                  return (
                    <button key={c.key} data-testid={`staff-perm-${m.id}-${c.key}`} onClick={() => togglePerm(m, c.key)}
                      className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${on ? "bg-[var(--qd-charcoal)] text-white border-[var(--qd-charcoal)]" : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"}`}>
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
        {staff.length === 0 && (
          <div className="qd-admin-card p-10 text-center text-gray-400 text-sm">
            No managers yet. Add one — they sign in at this same admin login with their own email and see only the sections you allow.
          </div>
        )}
      </div>

      <ActivityLog />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Add manager</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="staff-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input data-testid="staff-email" type="email" placeholder="Email (their login)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <PasswordInput data-testid="staff-password" placeholder="Password (leave blank to email an invite)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <div className="text-[11px] text-gray-400 -mt-1">Blank = they get a one-time link (valid 24h) to set their own password.</div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-gray-400 mb-2">Permissions</div>
              <div className="flex flex-wrap gap-2">
                {CAPS.map((c) => {
                  const on = !!form.permissions[c.key];
                  return (
                    <button key={c.key} type="button" data-testid={`staff-new-perm-${c.key}`}
                      onClick={() => setForm({ ...form, permissions: { ...form.permissions, [c.key]: !on } })}
                      className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${on ? "bg-[var(--qd-charcoal)] text-white border-[var(--qd-charcoal)]" : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"}`}>
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <Button data-testid="staff-save" onClick={create} disabled={busy} className="w-full bg-[var(--qd-charcoal)] hover:bg-black">{busy ? "Creating…" : "Create manager"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
